import React, { ReactNode } from 'react';
import { ToastContainer } from 'react-toastify';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ApplyStore, ApplyPersistor, UserStore } from '@apply/store';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import { ExpiredTokenModalV2 } from '@apply/ui';
import withAITracking from './ApplicationInsights';
import { ErrorBoundary } from './ErrorBoundary';
import { BusinessProfileWizard } from './businessProfile/BusinessProfileWizard';

//v2
import { ApplicationStyles } from './common/components/ApplicationStyles';
import { FeatureType } from '@apply/constants';
import { Home } from './home_v2/Home';
import DrawSummaryV2 from './draws_v2/DrawSummary/DrawSummary';
import { ControlDemo } from './v2/ControlDemo';
import { MobileReviewAndSubmit } from './draws_v2/MobileReviewAndSubmit';
import { MobileDrawSignPage } from './draws_v2/MobileDrawSignPage';
import { PrincipalApproveDrawPage } from './draws_v2/PrincipalApproveDrawPage';
import { LoginPage } from './Login/LoginPage';
import { useAuthentication } from './Login/hooks/useAuthentication';
import ForgotPasswordPageV2 from './v2/ForgotPasswordPageV2';
import ResetPasswordPageV2 from './v2/ResetPasswordPageV2';
import RegisterPageV2 from './v2/RegisterPageV2';
import { Borrower } from './application_v2/Borrower/Borrower';
import { Property } from './application_v2/Property/Property';
import { SelectBudget } from './application_v2/Budget/SelectBudget';
import { BudgetPage } from './application_v2/Budget/BudgetPage';
import { Review } from './application_v2/Review/Review';
import { BudgetLandPage } from './application_v2/Budget/BudgetLandPage';
import { BudgetFixFlipPage } from './application_v2/Budget/BudgetFixFlipPage';
import { Finalize } from './application_v2/Finalize/Finalize';
import { SelectSpecSheet } from './application_v2/SpecSheet/SelectSpecSheet';
import { SpecSheetPage } from './application_v2/SpecSheet/SpecSheetPage';
import { Appraisal } from './application_v2/Appraisal/Appraisal';
import { ThankYou } from './application_v2/ThankYou/ThankYou';
import { EntitySelectionPage } from './v2/EntitySelectionPage';
import { EstimatorPage } from './v2/EstimatorPage';
import { FileUploadPage } from './v2/FileUploadPage';
import { ManageUsers } from './manageusers/ManageUsers';
import NoticesPageV2 from './notices_v2/NoticesPageV2';
import { WaitOverlay } from '@apply/components';
import ExperiencePageV2 from './experience_v2/ExperiencePageV2/ExperiencePageV2';
import ExperiencePropertyPage from './experience_v2/ExperiencePropertyPage/ExperiencePropertyPage';
import { CustomPayment } from './customPayment/CustomPayment';
import { ResignAgreement } from './resignAgreement/ResignAgreement';

const RequireAuth = ({ children }: { children: ReactNode }) => {
    return UserStore.Types.retrieve() ? children : <Navigate to={`/login?redirectTo=${location.pathname}`} />;
};

const RequireAuthAsRegisteredApplicant = ({ children }: { children: ReactNode }) => {
    const auth = UserStore.Types.retrieve();
    if (!auth) {
        return <Navigate to={`/login?redirectTo=${location.pathname}`} />;
    } else {
        if (auth?.user?.isRegistered) {
            return children;
        } else {
            return <Navigate to={`/home`} />;
        }
    }
};

export const ApplyRouter: React.FC = () => {
    const oneHour = 3_600_000;
    const fiveMinutes = 300_000;

    const { logout } = useAuthentication();

    return (
        <>
            {UserStore.Types.retrieve() && (
                <ExpiredTokenModalV2
                    timeout={oneHour}
                    promptBeforeIdle={fiveMinutes}
                    onLogout={logout}
                />
            )}
            <Routes>
                <Route
                    path={'/review/:entryGuid/print'}
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <Review printMode={true} />
                        </>
                    }
                />
                <Route
                    path={'/budget/:entryGuid/print'}
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <BudgetPage printMode={true} />
                        </>
                    }
                />
                <Route
                    path={'/landBudget/:entryGuid/print'}
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <BudgetLandPage printMode={true} />
                        </>
                    }
                />
                <Route
                    path={'/fixFlipBudget/:entryGuid/print'}
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <BudgetFixFlipPage printMode={true} />
                        </>
                    }
                />
                <Route
                    path={'/specsheet/:entryGuid/print'}
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <SpecSheetPage printMode={true} />
                        </>
                    }
                />

                <Route
                    path={'/home'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <Home />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={'/controlDemo'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <ControlDemo />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={'/notices'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <NoticesPageV2 />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/experience'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                {/* Comment this in to Enable ExperienceV2 */}
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <ExperiencePageV2 />
                                {/* <ApplicationStyles />
                                <ExperiencePage /> */}
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path='/experience/property/:experienceId?'
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <ExperiencePropertyPage />
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/business/wizard/:step?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <BusinessProfileWizard />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/signagreement'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <ResignAgreement />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/borrower/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <Borrower />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/property/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <Property />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/budget/selectbudget/:entryGuid'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <SelectBudget />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/budget/budgetpage/:entryGuid'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <BudgetPage />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/budget/landbudgetpage/:entryGuid'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <BudgetLandPage />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/budget/fixflipbudgetpage/:entryGuid'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <BudgetFixFlipPage />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/selectspecsheet/:entryGuid'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <SelectSpecSheet />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/specsheetpage/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    featureType={FeatureType.ApplyV2Applications}
                                />
                                <SpecSheetPage />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/review/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <Review />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/thankyou'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <ThankYou />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/finalize/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <Finalize />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/appraisal/:entryGuid?'}
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <Appraisal />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path={'/draw/:drawBudgetId?'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <DrawSummaryV2 />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path={'/mobilereviewandsubmit/:drawBudgetId?'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <MobileReviewAndSubmit />
                            </>
                        </RequireAuth>
                    }
                />

                <Route
                    path={'/mobiledrawsign/:drawBudgetId/:drawId?'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <MobileDrawSignPage />
                            </>
                        </RequireAuth>
                    }
                />

                <Route
                    path={'/draw/:drawBudgetId/sign/:drawId'}
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <PrincipalApproveDrawPage />
                            </>
                        </RequireAuth>
                    }
                />

                <Route
                    path='/payment'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <CustomPayment />
                            {/* <ApplicationStyles />
                            <Payment /> */}
                        </>
                    }
                />
                <Route
                    path='/login'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <LoginPage />
                        </>
                    }
                />
                <Route
                    path='/manageusers'
                    element={
                        <RequireAuthAsRegisteredApplicant>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <ManageUsers />
                            </>
                        </RequireAuthAsRegisteredApplicant>
                    }
                />
                <Route
                    path='/entity-selection'
                    element={
                        <RequireAuth>
                            <>
                                <ApplicationStyles
                                    isFeatureDriven={true}
                                    manualV2StylesOverride={true}
                                />
                                <EntitySelectionPage />
                            </>
                        </RequireAuth>
                    }
                />
                <Route
                    path='/register'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <RegisterPageV2 gcRegistration={false} />
                        </>
                    }
                />
                <Route
                    path='/estimator'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <EstimatorPage />
                        </>
                    }
                />
                <Route
                    path='/file-upload'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <FileUploadPage />
                        </>
                    }
                />
                <Route
                    path='/gc-register'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <RegisterPageV2 gcRegistration={true} />
                        </>
                    }
                />
                <Route
                    path='/resetPassword'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <ResetPasswordPageV2 />
                        </>
                    }
                />
                <Route
                    path='/forgotPassword'
                    element={
                        <>
                            <ApplicationStyles
                                isFeatureDriven={true}
                                manualV2StylesOverride={true}
                            />
                            <ForgotPasswordPageV2 />
                        </>
                    }
                />

                <Route
                    path='/'
                    element={<Navigate to={'/home'} />}
                />
                <Route
                    path='*'
                    element={<Navigate to={'/home'} />}
                />
            </Routes>
        </>
    );
};

export const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <PersistGate
                persistor={ApplyPersistor}
                loading={null}
            >
                <Provider store={ApplyStore}>
                    <BrowserRouter>
                        <ApplyRouter />
                    </BrowserRouter>
                    <ToastContainer />
                    <WaitOverlay logoSrc='/assets/img/logo.webp.html' />
                </Provider>
            </PersistGate>
        </ErrorBoundary>
    );
};

export default withAITracking(App);
